import SaveIcon from '@mui/icons-material/Save';
import { Box, CircularProgress, Fab, Grid, Typography, useTheme } from '@mui/material';
import { vec3 } from 'gl-matrix';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from "react-router-dom";
import { Header, HeaderActionType } from '../Components/Header';
import { Inspector } from '../Components/Inspector';
import { HEADER_HEIGHT, VERSION } from '../constants';
import { isTokenValid } from '../helpers';
import { useDeleteAsset, useGetAsset, useGetAssetData, useGetGroupAssets, useUploadAsset } from '../hooks/useAsset';
import { useFetchScene, useUpdateScene } from '../hooks/useScenes';
import { default as Common, default as CommonRender } from '../Refinery/CommonRender';
import Light from '../Refinery/Lights/Light';
import { PointLight } from '../Refinery/Lights/PointLight';
import { MeshData } from '../Refinery/MeshManager';
import { Cube } from '../Refinery/Objects/Cube';
import { Mesh } from '../Refinery/Objects/Mesh';
import { Plane } from '../Refinery/Objects/Plane';
import { RenderObject } from '../Refinery/Objects/RenderObject';
import { Sphere } from '../Refinery/Objects/Sphere';
import { defaultMaterial, ObjectType } from '../Refinery/Objects/types';
import { SceneManager, SceneState } from '../Refinery/SceneManager';
import { ShaderType } from '../Refinery/ShaderManager';
import { Asset, ASSET_TYPE } from '../types/types';


export const Workspace = () => {
  const [params, setParams] = useSearchParams();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [sceneManager, setSceneManager] = useState<SceneManager>();
  const [render, setRender] = useState<boolean>(false);
  const [objectNum, setObjectNum] = useState<number>(0); // TODO - remove this, not used currently
  const animationFrameRef = useRef<number | null>(null);
  const { fetch: getAsset } = useGetAsset();
  const sceneId = params.get("sceneId");
  const [assetDataLoading, setAssetDataLoading] = useState<boolean>(true);
  const { put: updateSceneData, isLoading: updateSceneIsLoading } = useUpdateScene();
  const { deleteFn: deleteAsset } = useDeleteAsset();
  const { data, isLoading, error, refetch: refetchScene } = useFetchScene(sceneId ?? '');
  const { fetch: getAssetData } = useGetAssetData();
  const [initialLoad, setInitialLoad] = useState<boolean>(true);
  const [thumbnailIsLoading, setThumbnailIsLoading] = useState<boolean>(false);
  const { uploadAsset, loading, error: uploadAssetError } = useUploadAsset();
  const { data: groupAssetData, isLoading: groupAssetsLoading, refetch: groupAssetRefetch, error: groupAssetError } = useGetGroupAssets({ assetTypes: [ASSET_TYPE.MESH_DATA, ASSET_TYPE.TEXTURE_DATA] });
  const isLoggedIn = useMemo(() => isTokenValid(), [localStorage.getItem("jwt")]);
  const theme = useTheme();

  const fullyLoaded = useMemo(() => {
    return !isLoading && !groupAssetsLoading && !assetDataLoading;
  }, [isLoading, groupAssetsLoading, assetDataLoading]);

  useEffect(() => {
    if (groupAssetError) {
      setAssetDataLoading(false);
      setInitialLoad(false);
      return;
    }

    const shouldLoad =
      sceneManager &&
      sceneManager.getAssetManager() &&
      sceneManager.getTextureManager() &&
      sceneManager.getMeshManager() &&
      groupAssetData;

    if (!shouldLoad || !initialLoad) return;

    const loadAssets = async () => {
      try {
        setAssetDataLoading(true);

        const assetPromises = groupAssetData.map(async (a) => {
          const data = await getAssetData(`/assets/${a.id}`);
          a.data = data;
          sceneManager.getAssetManager().addAsset(a);

          if (a.type === ASSET_TYPE.TEXTURE_DATA) {
            const texture = await sceneManager.getTextureManager().loadTextureFromBuffer(sceneManager.getGLContext(), data);
            sceneManager.getTextureManager().addTexture(a.name, texture);
            return data;
          } else if (a.type === ASSET_TYPE.MESH_DATA) {
            const meshData = await Common.loadOBJ(new TextDecoder("utf-8").decode(data));
            sceneManager.getMeshManager().addMeshData(a.name, meshData);
            return "ok";
          }
        });

        await Promise.all(assetPromises);

      } catch (err) {
        console.error("Error loading assets:", err);
      } finally {
        setInitialLoad(false);
        setAssetDataLoading(false);
      }
    };

    loadAssets();
  }, [groupAssetData, sceneManager, setAssetDataLoading, groupAssetError, initialLoad]);

  useEffect(() => {
    if (error && error.message.includes("401")) {
      // Remove sceneId from query params
      params.delete("sceneId");
      setParams(params);
    }
  }, [error, params, setParams]);

  useEffect(() => {
    // initialize scene state
    if (!sceneManager) {
      const canvas = canvasRef.current;
      if (canvas) {
        const glCtx = canvas.getContext("webgl2", { stencil: true });
        if (glCtx && !sceneManager) {
          const ext = glCtx.getExtension("EXT_color_buffer_float");
          if (!ext) {
            alert("need EXT_color_buffer_float");
            return;
          }

          const floatExt = glCtx.getExtension("EXT_float_blend");
          if (!floatExt) {
            console.warn("EXT_float_blend not supported, disabling float blending");
          }

          const sm = new SceneManager(glCtx, canvas, (val) => { setRender(val) });
          sm.camera.init(canvas);
          sm.init().then(() => {
            // TODO - if there is no sceneId, we don't wanna create boiler plate
            if (!sceneId) {
              const material = { ...defaultMaterial };
              material.shaderType = ShaderType.BLINN_NO_TEXTURE;
              // add initial test cube
              sm?.addObject(new Cube(
                { name: `init${objectNum}`, material, position: vec3.fromValues(0, 0, 0) }));

              // add initial test light
              sm?.addLight(new PointLight({ name: 'test-light-0', position: vec3.fromValues(0.0, 4.0, 0.0), strength: 5 }));
            }

            setSceneManager(sm);
            document.getElementById('canvas')?.addEventListener('click', (event) => {
              const rect = canvas.getBoundingClientRect();
              sm.getPicker().mouseX = event.clientX - rect.left;
              sm.getPicker().mouseY = event.clientY - rect.top;
              sm.getPicker().active = true;
              setRender(true);
            });
          });
        }
      }
    }
  }, [objectNum, sceneId, sceneManager]);

  useEffect(() => {
    const unloadCallback = (event: any) => {
      event.preventDefault();
      event.returnValue = "";
      return "";
    };

    window.addEventListener("beforeunload", unloadCallback);
    return () => window.removeEventListener("beforeunload", unloadCallback);
  }, []);

  useEffect(() => {
    // we use scene name to prevent duplicative imports
    if (data && data.contents && !isLoading && sceneManager && sceneManager.sceneName !== data.name && !assetDataLoading) {
      // import the state we loaded from the fetch call
      try {
        const uploadedData: SceneState = JSON.parse(data.contents) as SceneState;
        sceneManager.import(uploadedData);
        sceneManager.sceneName = data.name;
      } catch (err) {
        console.error(err);
      }
    }
  }, [sceneManager, data, assetDataLoading, isLoading, initialLoad])

  useEffect(() => {
    if (!sceneManager) return;

    let lastFrameTime = performance.now();

    const renderLoop = (now: number) => {
      const deltaTime = (now - lastFrameTime) / 1000; // convert to seconds
      lastFrameTime = now;

      if (render) {
        sceneManager.render(deltaTime);
        sceneManager.camera.update(deltaTime);
      }
      animationFrameRef.current = requestAnimationFrame(renderLoop);
    };

    animationFrameRef.current = requestAnimationFrame(renderLoop);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [sceneManager, render]);

  return (
    <Grid
      container
      spacing={2}
      sx={(theme) => ({
        backgroundColor: theme.palette.background.paper,
        minHeight: '100vh'
      })}
    >
      <Grid size={12}>
        <Header
          drawerOpen
          isLoggedIn={isLoggedIn}
          activeActions={[HeaderActionType.LOGIN, HeaderActionType.ADMIN, HeaderActionType.DASHBOARD]}
        />
      </Grid>
      <Grid
        container
        size={12}
        paddingTop={`${HEADER_HEIGHT}px`}
        flexDirection='row'
        ml="15px"
        mr="15px"
        mb="15px"
        sx={{
          height: "80% !important"
        }}
      >
        <Grid
          id="canvas-parent"
          size={8}
          height="80vh"
          sx={{
            position: 'relative', // allows absolute positioning of overlay
          }}
        >
          <canvas
            style={{
              width: '100%',
              height: '100%',
              display: 'block',
              borderRadius: theme.shape.borderRadius
            }}
            ref={canvasRef}
            tabIndex={0}
            id="canvas"
          />
          {!fullyLoaded && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                bgcolor: 'rgba(0,0,0,1.0)', // dim background
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2,
              }}
            >
              <CircularProgress color="primary" />
            </Box>
          )}
        </Grid>
        <Grid size={4}>
          <Inspector
            isLoggedIn={isLoggedIn}
            textureManager={sceneManager?.getTextureManager()}
            meshManager={sceneManager?.getMeshManager()}
            open
            objects={sceneManager?.objects ?? {}}
            lights={sceneManager?.lights ?? {}}
            selectedObject={sceneManager?.selected}
            assets={groupAssetData ?? []}
            sceneData={sceneManager?.getSceneSettingsData() ?? {}}
            onSelect={(v) => {
              sceneManager?.setSelected(v);
              sceneManager?.refresh();
            }}
            onSceneSettingsChange={(data) => {
              if (data.backgroundColour) {
                sceneManager?.getRenderer().setBackgroundColour(data.backgroundColour);
                sceneManager?.refresh();
              }
            }}
            onAssetUpload={async (assetId, _) => {
              // TODO - move the upload call here and then parse first then upload, if parse fails we just dont upload. future us problem
              try {
                const assetDetails = await getAsset(`/assets/${assetId}`);
                const assetData = await getAssetData(`/assets/${assetId}`);
                assetDetails.data = assetData;

                sceneManager?.getAssetManager().addAsset(assetDetails);
                if (assetDetails.type === ASSET_TYPE.TEXTURE_DATA) {
                  const texture = await sceneManager?.getTextureManager().loadTextureFromBuffer(sceneManager.getGLContext(), assetData);
                  if (!texture) {
                    // TODO - throwup a big error
                    console.error("unable to create texture from uploaded resource");
                    return;
                  };
                  sceneManager?.getTextureManager().addTexture(assetDetails.name, texture);
                } else if (assetDetails.type === ASSET_TYPE.MESH_DATA) {
                  const decoder = new TextDecoder("utf-8");
                  const text = decoder.decode(assetData);
                  const meshData = await CommonRender.loadOBJ(text);
                  sceneManager?.getMeshManager().addMeshData(assetDetails.name, meshData);
                }
                await groupAssetRefetch();
              } catch (err) {
                // TODO - make error better
                console.error(err);

                // delete the failed asset since it was no good
                try {
                  await deleteAsset(assetId);
                  await groupAssetRefetch();
                } catch (err) {
                  console.error(err)
                }
              }
            }}
            onAssetDelete={async (asset: Asset) => {
              await deleteAsset(asset.id);
              await groupAssetRefetch();
              sceneManager?.getAssetManager().deleteAsset(asset.id);

              if (asset.type === ASSET_TYPE.TEXTURE_DATA) {
                sceneManager?.getTextureManager().deleteTexture(asset.name);
              } else if (asset.type === ASSET_TYPE.MESH_DATA) {
                sceneManager?.getMeshManager().deleteMesh(asset.name);
              }
            }}
            onSceneUpload={(data) => {
              // try and parse it 
              try {
                const uploadedData: SceneState = JSON.parse(data) as SceneState;
                sceneManager?.import(uploadedData);
              } catch (err) {
                console.error(err);
              }
            }}
            onSceneDownload={() => {
              const currentState = sceneManager?.export();
              const currentStateString = JSON.stringify(currentState);

              let aDownload = document.createElement('a');
              let sceneName = sceneManager?.sceneName
                ? sceneManager?.sceneName
                : "scene";

              aDownload.href =
                "data:text/json;charset=utf-8," + encodeURIComponent(currentStateString);
              aDownload.download = `${sceneName}.json`;
              aDownload.click();
              aDownload.remove();
            }}
            onAdd={async (type, name, meshKey) => {
              // TODO - move this into its own func so cleaner
              const position = vec3.fromValues(0, 0, 0);

              if (type === ObjectType.CUBE) {
                sceneManager?.addObject(new Cube(
                  { name: name, material: defaultMaterial, position }));
                setObjectNum(objectNum + 1);
              } else if (type === ObjectType.MESH) {
                let meshLookupName = meshKey ?? 'crane.obj';
                let meshData: MeshData | undefined = sceneManager?.getMeshManager().getMeshData(meshLookupName);

                if (!meshData) {
                  const meshString = await Common.fetchOBJFile(meshLookupName);
                  const craneData = await Common.loadOBJ(meshString);
                  sceneManager?.getMeshManager().addMeshData(meshLookupName, craneData);

                  // finally add the default mesh in
                  const mesh = new Mesh(
                    { name: name, fileName: meshLookupName, material: defaultMaterial, position, objData: craneData })
                  sceneManager?.addObject(mesh);
                  setObjectNum(objectNum + 1);
                } else {
                  // load in the mesh
                  const mesh = new Mesh(
                    { name: name, fileName: meshLookupName, material: defaultMaterial, position, objData: meshData })
                  sceneManager?.addObject(mesh);
                  setObjectNum(objectNum + 1);
                }

              } else if (type === ObjectType.PLANE) {
                sceneManager?.addObject(new Plane(
                  { name: name, material: defaultMaterial, position }));
                setObjectNum(objectNum + 1);
              } else if (type === ObjectType.SPHERE) {
                sceneManager?.addObject(new Sphere(
                  { name: name, material: defaultMaterial, position }));
                setObjectNum(objectNum + 1);
              } else if (type === "light") {
                sceneManager?.addLight(new Light({ name }));
              }
            }}
            onSelectedChange={(v) => {
              if (sceneManager?.selected) {
                // handle object changes
                if (sceneManager.selected instanceof RenderObject && v.objectData) {
                  if (v.objectData.model) {
                    sceneManager.selected.model = v.objectData.model;
                  }
                  if (v.objectData.material) {
                    sceneManager.selected.setMaterial(v.objectData.material)
                  }
                  if (v.objectData.parent) {
                    sceneManager.selected.parent = v.objectData.parent;
                  }
                  // reset the object if this flag is set
                  if (v.objectData.reset) {
                    sceneManager.resetSelected();
                  }
                } else if (sceneManager.selected instanceof Light && v.lightData) {
                  sceneManager.selected.position = v.lightData.position;
                  sceneManager.selected.colour = v.lightData.colour;
                  sceneManager.selected.strength = v.lightData.strength;
                }

                sceneManager.refresh();
              }
              sceneManager?.refresh();

            }}
            onSelectedClone={async () => {
              if (sceneManager && sceneManager.selected) {
                sceneManager?.cloneObject(sceneManager.selected);
                sceneManager.refresh();
              }
            }}
            onSelectedDelete={async () => {
              if (sceneManager && sceneManager.selected) {
                sceneManager?.deleteObject(sceneManager.selected);
                sceneManager.refresh();
              }
            }}
            onSelectedRename={(newName, type) => {
              if (!sceneManager || !sceneManager.selected || !sceneManager.selected.name) {
                return
              }

              const originalName = sceneManager?.selected.name;
              sceneManager.selected.name = newName;
              if (type === "light") {
                sceneManager.lights[newName] = sceneManager.lights[originalName];
                delete sceneManager.lights[originalName];
              } else if (type === "object") {
                sceneManager.objects[newName] = sceneManager.objects[originalName];
                delete sceneManager.objects[originalName];
              }
            }}
          />
        </Grid>
      </Grid>

      {isLoggedIn && (
        <Fab
          disabled={updateSceneIsLoading || thumbnailIsLoading}
          sx={(theme) => ({
            backgroundColor: theme.palette.primary.main,
            position: "fixed",
            bottom: 16,  // distance from bottom
            right: 16,   // distance from right
            zIndex: 1000, // keep above most content
          })}
          onClick={async () => {
            if (!sceneId || !data)
              return;

            setThumbnailIsLoading(true);
            if (data.thumbnailId) {
              try {
                await deleteAsset(data?.thumbnailId);
              } catch (err) {
                // TODO - handle error a bit better
                console.error(err);
              }
            }

            // we should remove the old thumbnail first, then upload the one
            const canvas = canvasRef.current;
            if (!canvas) {
              return;
            }
            const gl = canvas.getContext("webgl2");
            if (!gl) return;
            const file = await Common.captureWebGL2Canvas(canvas, gl, sceneId);
            const metadata = {
              name: file.name, type: ASSET_TYPE.THUMBNAIL_DATA
            };
            const result = await uploadAsset(file, metadata);

            try {
              const currentState = sceneManager?.export();
              const currentStateString = JSON.stringify(currentState);
              await updateSceneData({ sceneId: sceneId, contents: currentStateString, thumbnailId: result.data?.assetId ?? 'unknown' });
              await groupAssetRefetch();
            } catch (err) {
              console.error(err);
            }
            await refetchScene();
            setThumbnailIsLoading(false);
          }}
        >
          {updateSceneIsLoading || thumbnailIsLoading && (
            <CircularProgress sx={(theme) => ({
              color: theme.palette.text.primary
            })} />
          )}
          {!updateSceneIsLoading && !thumbnailIsLoading && (
            <SaveIcon sx={(theme) => ({
              color: theme.palette.text.primary
            })} />
          )}
        </Fab>
      )}
      <Grid size={12}>
        <Typography color="textSecondary">{`Version: ${VERSION}`}</Typography>
      </Grid>
    </Grid>
  );
};
